import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { GETAPI, POSTAPIFORMDATA } from "../../api/Api";
import { ApiConstants } from "../../constants/ApiConstants";
import Spinner from "../../components/Spinner";
import { GetProfileAction } from "../../redux/Action/ProfileAction";
import { saveToStorage } from "../../utils/Helper";
import { StorageConstants } from "../../constants/Constants";
import SubscriptionBankSummary from "../Subscription/SubscriptionBankSummary";
import SubscriptionPlanFeaturesList from "../Subscription/SubscriptionPlanFeaturesList";
import { OfflineSubscriptionUserStatus } from "../../utils/subscriptionAccess";

type Props = {
  onSkip: () => void;
  onSubmitSuccess: () => void;
};

const pickErrorMessage = (res: any): string => {
  const m = res?.message;
  if (typeof m === "string" && m.trim()) return m;
  if (m && typeof m === "object" && typeof m.message === "string") return m.message;
  return "";
};

/** Step after complete-profile form: bank details + optional subscription payment request (same API as subscription purchase). */
const ProfileDetailsSubscriptionStep = ({ onSkip, onSubmitSuccess }: Props) => {
  const dispatch = useDispatch<any>();
  const paymentFormId = "profile-subscription-payment-form";

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<any[]>([]);
  const [bank, setBank] = useState<any>(null);
  const [salonSub, setSalonSub] = useState<any>(null);
  const [pendingRequest, setPendingRequest] = useState<any>(null);
  const [planId, setPlanId] = useState<string>("");
  const [transactionId, setTransactionId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      const plansRes: any = await GETAPI(ApiConstants.OfflineSubscriptionPlans, {}, false);
      const bankRes: any = await GETAPI(ApiConstants.OfflineSubscriptionBankDetails, {}, false);
      if (cancelled) return;
      if (plansRes?.status === true && Array.isArray(plansRes?.data?.plans)) {
        setPlans(plansRes.data.plans);
        setSalonSub(plansRes.data.salon_subscription ?? null);
        setPendingRequest(plansRes.data.pending_request ?? null);
        const list = plansRes.data.plans as any[];
        if (list.length > 0) {
          setPlanId(String(list[0].id));
        }
      } else {
        setPlans([]);
        setLoadError(
          pickErrorMessage(plansRes) ||
            "Unable to load subscription plans. Please try again or contact support.",
        );
      }
      if (bankRes?.status === true && bankRes?.data) {
        setBank(bankRes.data);
      } else {
        setBank(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSuspended =
    Number(salonSub?.offline_subscription_status) ===
      OfflineSubscriptionUserStatus.SUSPENDED;

  const subSt = Number(salonSub?.offline_subscription_status);
  const pendingVerifyOnly =
    subSt === OfflineSubscriptionUserStatus.PENDING_VERIFY;
  const hasPendingUpgradeOnProfile =
    !!pendingRequest && subSt === OfflineSubscriptionUserStatus.ACTIVE;
  /** Block another payment submit (first purchase pending, or upgrade already queued). */
  const hasPendingFlow =
    pendingVerifyOnly || hasPendingUpgradeOnProfile;

  const selectedPlan = plans.find((p) => String(p.id) === planId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuspended || hasPendingFlow || !planId || !file || !transactionId.trim()) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("plan_id", planId);
    fd.append("transaction_id", transactionId.trim());
    fd.append("payment_proof", file);
    const res: any = await POSTAPIFORMDATA(
      ApiConstants.OfflineSubscriptionSubmitPayment,
      fd,
      true,
    );
    setSubmitting(false);
    if (res?.status) {
      const profile: any = await GETAPI(ApiConstants.GetProfile, {}, false);
      if (profile?.status && profile?.data) {
        saveToStorage({ key: StorageConstants.USER_DETAIL, value: profile.data });
      }
      dispatch(GetProfileAction({}));
      onSubmitSuccess();
    }
  };

  const actionsSkip = (
    <button
      type="button"
      className="profile-subscription-step__btn-skip"
      onClick={onSkip}
    >
      <FormattedMessage id="skip" defaultMessage="Skip" />
    </button>
  );

  const actionsSubmit = (
    <button
      type="submit"
      form={paymentFormId}
      className="btn btn-fill profile-subscription-step__btn-submit"
      disabled={submitting}
    >
      {submitting ? (
        <FormattedMessage id="submitting" defaultMessage="Submitting…" />
      ) : (
        <FormattedMessage id="submit" defaultMessage="Submit" />
      )}
    </button>
  );

  return (
    <div className="profileDetailsBox profileDetailsBox--subscription-step">
      <div className="profile-subscription-step">
        {loading ? (
          <div className="py-5 text-center">
            <Spinner />
          </div>
        ) : loadError ? (
          <div className="alert alert-warning profile-subscription-step__load-error" role="alert">
            <p className="mb-3">{loadError}</p>
            {actionsSkip}
          </div>
        ) : (
          <>
            <header className="profile-subscription-step__intro">
              <p className="text-muted mb-0">
                <FormattedMessage
                  id="profile_subscription_step_intro"
                  defaultMessage="Transfer the plan amount using the bank details below, then submit your transaction ID and payment proof for admin review. You can skip and do this later from your account."
                />
              </p>
            </header>

            {isSuspended && (
              <div className="alert alert-danger mb-4" role="alert">
                <FormattedMessage
                  id="subscription_suspended_no_payment"
                  defaultMessage="Your account is suspended. You cannot submit a new payment until an administrator reactivates your subscription. See subscription plans for details."
                />
              </div>
            )}
            {pendingVerifyOnly && !isSuspended && (
              <div className="alert alert-info mb-4" role="alert">
                <FormattedMessage
                  id="profile_subscription_pending_already"
                  defaultMessage="A payment request is already pending verification. You can continue to the home page."
                />
              </div>
            )}
            {hasPendingUpgradeOnProfile && !isSuspended && (
              <div className="alert alert-info mb-4" role="alert">
                <FormattedMessage
                  id="profile_subscription_upgrade_pending"
                  defaultMessage="An upgrade payment is already awaiting admin review. Your current subscription stays active. You can continue to the home page."
                />
              </div>
            )}

            <div className="row g-4 g-lg-4 align-items-stretch">
              <div className="col-12 col-lg-6">
                <div className="profile-subscription-step__card">
                  <h3>
                    <FormattedMessage id="bank_transfer_details" defaultMessage="Bank transfer details" />
                  </h3>
                  <SubscriptionBankSummary bank={bank} />
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="profile-subscription-step__card">
                  <h3>
                    <FormattedMessage id="your_selection" defaultMessage="Your selection" />
                  </h3>
                  {plans.length === 0 ? (
                    <>
                      <p className="small text-muted mb-4">
                        <FormattedMessage
                          id="profile_subscription_no_plans"
                          defaultMessage="No subscription plans are available yet. You can skip for now."
                        />
                      </p>
                      <div className="profile-subscription-step__actions border-0 pt-0 mt-0">
                        {actionsSkip}
                      </div>
                    </>
                  ) : (
                    <>
                      {plans.length > 1 && (
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            <FormattedMessage id="subscription_plan" defaultMessage="Plan" />
                          </label>
                          <select
                            className="form-select"
                            style={{ borderRadius: "12px", borderColor: "#DFD8CB" }}
                            value={planId}
                            onChange={(e) => setPlanId(e.target.value)}
                          >
                            {plans.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} — {Number(p.price).toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {selectedPlan && (
                        <div className="profile-subscription-step__plan-summary">
                          <p className="fw-semibold mb-2">{selectedPlan.name}</p>
                          {selectedPlan.overview ? (
                            <p className="text-muted small mb-3">{selectedPlan.overview}</p>
                          ) : null}
                          <p className="small text-muted mb-1">
                            <FormattedMessage id="amount" defaultMessage="Amount" />:{" "}
                            <strong className="text-dark">{Number(selectedPlan.price).toFixed(2)}</strong>
                          </p>
                          <p className="small text-muted mb-3">
                            <FormattedMessage id="validity" defaultMessage="Validity" />:{" "}
                            {selectedPlan.validity_days}{" "}
                            <FormattedMessage id="days" defaultMessage="days" />
                          </p>
                          <SubscriptionPlanFeaturesList
                            plan={selectedPlan}
                            titleClassName="h6 mb-2"
                            listClassName="mb-0"
                          />
                        </div>
                      )}
                      {!isSuspended && !hasPendingFlow && selectedPlan && (
                        <div className="profile-subscription-step__payment-block">
                          <h4>
                            <FormattedMessage id="payment_proof_section" defaultMessage="After payment" />
                          </h4>
                          <p className="small text-muted mb-3">
                            <FormattedMessage
                              id="payment_outside_note"
                              defaultMessage="Complete the transfer in your bank app or website, then enter the transaction ID and upload your receipt (PNG or JPEG)."
                            />
                          </p>
                          <form id={paymentFormId} onSubmit={onSubmit}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold">
                                <FormattedMessage id="transaction_id" defaultMessage="Transaction ID" />
                              </label>
                              <input
                                className="form-control"
                                style={{ borderRadius: "12px", borderColor: "#DFD8CB", padding: "0.65rem 1rem" }}
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                required
                              />
                            </div>
                            <div className="mb-0">
                              <label className="form-label fw-semibold">
                                <FormattedMessage id="payment_proof" defaultMessage="Payment proof" />
                              </label>
                              <input
                                type="file"
                                className="form-control"
                                style={{ borderRadius: "12px", borderColor: "#DFD8CB", padding: "0.55rem 0.85rem" }}
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                required
                              />
                            </div>
                          </form>
                        </div>
                      )}
                      {(isSuspended || hasPendingFlow) && (
                        <div className="profile-subscription-step__actions">{actionsSkip}</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isSuspended && !hasPendingFlow && plans.length > 0 && selectedPlan && (
              <div
                className="profile-subscription-step__actions"
                role="group"
                aria-label="Subscription actions"
              >
                {actionsSkip}
                {actionsSubmit}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileDetailsSubscriptionStep;
